import { Box, Card, Container, Grid, Typography } from '@material-ui/core';
import { CopyToClipboard, JobListItem, Page, SocialShareButtons, Spinner } from 'components';
import { DialogQrCode } from 'components/dialog-qr-code';
import { useNotifications } from 'context/NotificationsContext';
import createDOMPurify from 'dompurify';
import { GoogleAnaliticsEvent } from 'enum';
import { Company as ICompany, Job, useCompanyQuery } from 'graphql/generated';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { logAnalyticsEvent } from 'ui-services/analytics.ui-service';
import NotFound from 'views/not-found';
import { useStyles } from './company-detail.style';
import { CompanyHeader } from './company-header';
import { CompanySummary } from './company-summary';

const DOMPurify = createDOMPurify(window);

export const CompanyDetail: React.FC = () => {
    const classes = useStyles();

    const { t } = useTranslation();
    const { id } = useParams<any>();
    const { notify } = useNotifications();

    const slug = window.location.pathname.replace('/', '');

    const { data, loading, error } = useCompanyQuery({
        fetchPolicy: 'network-only',
        variables: { id, slug },
    });

    const company = data?.company;
    const jobs = company?.jobs?.edges || [];
    const onCopied = (): void => {
        notify({
            message: t('jobDetails:copyLinkSuccess'),
            type: 'success',
        });
    };

    useEffect(() => {
        logAnalyticsEvent(GoogleAnaliticsEvent.COMPANY_PAGE_VIEWED);
    }, []);

    if (loading) {
        return <Spinner />;
    }

    if (error) {
        return <NotFound />;
    }

    return (
        <Page title={company?.name} style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
            <CompanyHeader company={company as ICompany} />
            <Container className={classes.container} maxWidth="lg">
                <Grid container item xs={12} style={{ position: 'relative' }}>
                    <Grid item md={8} xs={12} className={classes.leftSide}>
                        {company?.description && (
                            <div className={classes.content}>
                                <div className={classes.contentSection}>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(company?.description),
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        <Card className={classes.postingsContainer}>
                            <Box className={classes.headline}>
                                <Typography variant="h5" color="textSecondary">
                                    {t('companyDetail:openPositions')}
                                </Typography>
                            </Box>
                            <Grid container>
                                {jobs.length > 0 ? (
                                    jobs.map(({ node: job }) => (
                                        <Grid item xs={12} key={job?.id}>
                                            <JobListItem showCompany={false} job={job as Job} />
                                        </Grid>
                                    ))
                                ) : (
                                    <Grid item xs={12}>
                                        <Box
                                            flexGrow={1}
                                            p={4}
                                            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                        >
                                            <Typography variant="subtitle1">
                                                {t('jobDetails:noOpenPositions')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Card>
                    </Grid>
                    <Grid item md={4} xs={12} className={classes.sticky}>
                        <div className={classes.rightSide}>
                            <div className={classes.sideSection}>
                                <Box className={classes.summary}>
                                    <CompanySummary company={company as ICompany} />
                                </Box>
                            </div>
                            <div style={{ marginBottom: '10px' }} className={classes.sideSection}>
                                <Typography variant="h5" color="textSecondary">
                                    {t('jobDetails:share')}
                                </Typography>
                            </div>
                            <div style={{ marginBottom: '10px' }} className={classes.sideSection}>
                                <SocialShareButtons pageType="company" url={window.location.href}>
                                    <DialogQrCode description={t('companyDetail:qrDescription')} />
                                </SocialShareButtons>
                            </div>
                            <div className={classes.sideSection}>
                                <CopyToClipboard onCopied={onCopied} value={window.location.href} />
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </Container>
        </Page>
    );
};
